'use client';

import * as React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';

const ToastProvider = ToastPrimitives.Provider;

// eslint-disable-next-line @eslint-react/no-forward-ref -- wraps Radix UI primitive; forwardRef required for Radix's ref-forwarding API
const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed top-4 right-4 z-[10004] flex max-h-screen w-full flex-col gap-2 p-4 sm:flex-col md:max-w-[420px]',
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  // ring-1 (on top of the variant border) is the toast-specific depth cue: a
  // floating overlay must read as clearly separate from in-page glass panels
  // that share the same translucent background tint — without it, a toast
  // sitting over a modal header was misread as page content, e.g. "assignee
  // chips" (PROD, LSH Expert phone recording, 2026-08-20).
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-xl border ring-1 ring-black/5 dark:ring-white/10 p-5 pr-8 shadow-2xl backdrop-blur-[7px] dark:backdrop-blur-[15px] transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-right',
  {
    variants: {
      variant: {
        default:
          'border-brand-accent/20 bg-white/12 dark:bg-white/[0.03] text-gray-900 dark:text-white shadow-[0_8px_32px_rgba(251,191,36,0.15)]',
        destructive:
          'destructive group border-red-500/30 bg-red-50/12 dark:bg-red-950/[0.03] text-red-900 dark:text-red-50 shadow-[0_8px_32px_rgba(239,68,68,0.2)]',
        success:
          'border-emerald-500/30 bg-emerald-50/12 dark:bg-white/[0.03] text-emerald-900 dark:text-emerald-50 shadow-[0_8px_32px_rgba(16,185,129,0.18)]',
        error:
          'border-red-500/30 bg-red-50/12 dark:bg-white/[0.03] text-red-900 dark:text-red-50 shadow-[0_8px_32px_rgba(239,68,68,0.2)]',
        // Org-branded (not hardcoded blue): hue follows --brand-h via the
        // `brand-*` Tailwind tokens; glow uses the JS-set --color-primary-rgb
        // triplet. Faithful hue swap of the former sky variant (same opacity
        // /lightness steps) so contrast behavior is unchanged per org. This is
        // the variant the Undo toast (useUndoAction) renders in.
        info: 'border-brand-500/30 bg-brand-50/12 dark:bg-white/[0.03] text-brand-900 dark:text-brand-50 shadow-[0_8px_32px_rgba(var(--color-primary-rgb),0.18)]',
        warning:
          'border-amber-500/30 bg-amber-50/12 dark:bg-white/[0.03] text-amber-900 dark:text-amber-50 shadow-[0_8px_32px_rgba(245,158,11,0.18)]',
        loading:
          'border-white/20 bg-white/12 dark:bg-white/[0.03] text-gray-900 dark:text-white shadow-[0_8px_32px_rgba(148,163,184,0.15)]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

// eslint-disable-next-line @eslint-react/no-forward-ref -- wraps Radix UI primitive; forwardRef required for Radix's ref-forwarding API
const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants> & {
      /** Optional click handler - makes toast clickable (cursor changes) */
      onClick?: () => void;
    }
>(({ className, variant, onClick, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(
        toastVariants({ variant }),
        onClick && 'cursor-pointer hover:brightness-110',
        className
      )}
      onClick={onClick}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

// eslint-disable-next-line @eslint-react/no-forward-ref -- wraps Radix UI primitive; forwardRef required for Radix's ref-forwarding API
const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive',
      className
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

// eslint-disable-next-line @eslint-react/no-forward-ref -- wraps Radix UI primitive; forwardRef required for Radix's ref-forwarding API
const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600',
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

// eslint-disable-next-line @eslint-react/no-forward-ref -- wraps Radix UI primitive; forwardRef required for Radix's ref-forwarding API
const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn('text-sm font-semibold tracking-tight', className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

// eslint-disable-next-line @eslint-react/no-forward-ref -- wraps Radix UI primitive; forwardRef required for Radix's ref-forwarding API
const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn('text-xs opacity-80 leading-relaxed', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
